import React, { useEffect, useState } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Download, Filter, Search, User, Calculator, Plus, X, Calendar, Eye } from 'lucide-react';

// const API_URL = 'http://localhost:5551';
// const API_URL = 'https://attendance.nadezhda.in';
const API_URL = '/api';

interface ReportData {
  name: string;
  designation: string;
  date: string;
  in_time: string;
  out_time: string;
  status: string;
  remark: string;
}

interface Employee {
  id: number;
  
  name: string;
  joining_date: string;
  leaving_date: string | null;
}

const ReportPage = () => {
  const [reportData, setReportData] = useState<ReportData[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const todayStr = new Date().toISOString().split('T')[0];
  const [reportRange, setReportRange] = useState({ start: '2026-02-01', end: todayStr });
  
  // Filters & Config
  const [selectedEmp, setSelectedEmp] = useState('ALL');
  const [holidays, setHolidays] = useState<string[]>(['2026-01-26']);
  const [newHoliday, setNewHoliday] = useState('');
  const [includeSundays, setIncludeSundays] = useState(true);

  // Modal State for Drill-down
  const [modalData, setModalData] = useState<{ title: string; dates: string[]; type: string } | null>(null);

  useEffect(() => {
    fetchEmployees();
    fetchReport();
  }, []);

  // UPDATE: Fetch ALL employees (past and present) for the report
  const fetchEmployees = async () => axios.get(`${API_URL}/employees/all`).then(res => setEmployees(res.data));
  
  const fetchReport = async () => {
    try {
      const res = await axios.get(`${API_URL}/report?startDate=${reportRange.start}&endDate=${reportRange.end}`);
      setReportData(Array.isArray(res.data) ? res.data : []);
    } catch (e) { console.error(e); }
  };

  // --- CORE LOGIC: CALCULATE ATTENDANCE & DATES ---
  const calculateAttendanceSummary = () => {
    const staffToCalc = selectedEmp === 'ALL' ? employees : employees.filter(e => e.name === selectedEmp);

    return staffToCalc.map(emp => {
      let totalDaysInRange = 0;
      
      // Tracking Dates for Popups
      const presentDates: string[] = [];
      const absentDates: string[] = [];
      const leaveDates: string[] = []; // Sundays + Holidays

      let current = new Date(reportRange.start);
      const end = new Date(reportRange.end);

      while (current <= end) {
        const dateStr = current.toISOString().split('T')[0];
        const dayOfWeek = current.getDay(); // 0 = Sunday
        const isEmployed = dateStr >= emp.joining_date && (!emp.leaving_date || dateStr <= emp.leaving_date);

        if (isEmployed) {
          totalDaysInRange++;
          const isSunday = dayOfWeek === 0;
          const isHoliday = holidays.includes(dateStr);

          // 1. Check Paid Leaves (Sundays/Holidays)
          if ((isSunday && includeSundays) || isHoliday) {
            leaveDates.push(dateStr + (isSunday ? ' (Sunday)' : ' (Holiday)'));
          } else {
            // 2. Check DB Status
            const record = reportData.find(r => r.name === emp.name && r.date === dateStr);
            const status = record ? record.status : 'ABSENT';

            if (status === 'Present' || status === 'Half Day' || status === 'Working') {
              presentDates.push(`${dateStr} [${status}]`);
            } else {
              if(!isSunday) { // Agar Sunday count nahi ho raha to wo Absent me jayega agar kaam nahi kiya
                  absentDates.push(dateStr);
              }
            }
          }
        }
        current.setDate(current.getDate() + 1);
      }

      return {
        name: emp.name,
        total_period: totalDaysInRange,
        paid_leaves_count: leaveDates.length,
        worked_count: presentDates.length,
        absent_count: absentDates.length,
        payable_days: leaveDates.length + presentDates.length,
        
        // Detailed Arrays for Popup
        details: {
          present: presentDates,
          absent: absentDates,
          leaves: leaveDates
        }
      };
    }).filter(data => data.total_period > 0); // UPDATE: Filter out employees who have 0 days in this date range
  };

  const summaryData = calculateAttendanceSummary();

  // --- PDF GENERATION (Summary + Details) ---
  const downloadPDF = () => {
    const doc = new jsPDF();
    
    // 1. HEADER
    doc.setFontSize(18);
    doc.setTextColor(41, 58, 128);
    doc.text("Nadezhda Attendance Report", 14, 20);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Period: ${reportRange.start} to ${reportRange.end}`, 14, 26);

    // 2. SUMMARY TABLE (Top)
    const summaryBody = summaryData.map(s => [
      s.name, 
      s.total_period, 
      s.paid_leaves_count, 
      s.worked_count, 
      s.absent_count, 
      s.payable_days
    ]);

    autoTable(doc, {
      startY: 35,
      head: [['Employee', 'Total Days', 'Offs/Holidays', 'Present', 'Absent', 'PAYABLE']],
      body: summaryBody,
      theme: 'grid',
      headStyles: { fillColor: [41, 58, 128] },
      styles: { fontSize: 10, halign: 'center' },
      columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } } // Name align left
    });

    // 3. DETAILED LOGS HEADER (Below Summary)
    // @ts-ignore
    let finalY = doc.lastAutoTable.finalY + 15; // Summary table ke niche start hoga
    doc.setFontSize(14);
    doc.setTextColor(0);
    doc.text("Detailed Daily Logs", 14, finalY);

    // 4. DETAILED TABLE (Grouped by Date Logic preserved)
    const tableBody: any[] = [];
    
    // Filter data based on selected employee for PDF too
    let pdfDetailData = reportData;
    if(selectedEmp !== 'ALL') {
        pdfDetailData = reportData.filter(d => d.name === selectedEmp);
    }
    
    // Sort by Date DESC
    pdfDetailData.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    let lastDate: string | null = null;

    pdfDetailData.forEach((row) => {
      const dateObj = new Date(row.date);
      const dateWithDay = dateObj.toLocaleDateString('en-GB', { 
        weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' 
      });

      if (dateWithDay !== lastDate) {
        tableBody.push([{ 
          content: dateWithDay, colSpan: 5, 
          styles: { fillColor: [240, 240, 240], fontStyle: 'bold', textColor: [50, 50, 50] } 
        }]);
        lastDate = dateWithDay;
      }

      tableBody.push([
        row.name, 
        row.in_time || '-', 
        row.out_time || '-', 
        row.status, 
        row.remark || ''
      ]);
    });

    autoTable(doc, {
      startY: finalY + 5,
      head: [['Employee', 'In Time', 'Out Time', 'Status', 'Remark']],
      body: tableBody,
      theme: 'grid',
      styles: { fontSize: 9 },
      headStyles: { fillColor: [100, 100, 100] } // Gray header for details
    });

    doc.save(`Full_Report_${reportRange.start}.pdf`);
  };

  const addHoliday = () => { if (newHoliday && !holidays.includes(newHoliday)) { setHolidays([...holidays, newHoliday]); setNewHoliday(''); }};
  const removeHoliday = (d: string) => setHolidays(holidays.filter(x => x !== d));

  // Open Modal Helper
  const openModal = (title: string, dates: string[], type: string) => {
    setModalData({ title, dates, type });
  };

  return (
    <div className="bg-slate-50 min-h-screen p-6 font-sans">
      
      {/* 1. CONFIGURATION CARD */}
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 mb-6">
        <h2 className="text-lg font-black text-slate-700 flex items-center gap-2 mb-4">
          <Calculator className="text-blue-600" /> Salary Configuration
        </h2>
        <div className="flex flex-wrap gap-6 items-end">
          <div className="flex items-center gap-2 bg-slate-100 p-2 rounded-xl border border-slate-200">
            <input type="date" value={reportRange.start} onChange={e=>setReportRange({...reportRange, start:e.target.value})} className="bg-transparent text-sm font-bold outline-none text-slate-600" />
            <span className="text-slate-400">-</span>
            <input type="date" value={reportRange.end} onChange={e=>setReportRange({...reportRange, end:e.target.value})} className="bg-transparent text-sm font-bold outline-none text-slate-600" />
            <button onClick={fetchReport} className="bg-blue-600 text-white p-2 rounded-lg ml-2 hover:bg-blue-700 shadow-md"><Search size={14}/></button>
          </div>

          <div className="flex-1">
             <div className="flex gap-2 mb-2">
               <input type="date" value={newHoliday} onChange={e=>setNewHoliday(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm outline-none" />
               <button onClick={addHoliday} className="bg-emerald-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 hover:bg-emerald-700"><Plus size={14}/> Holiday</button>
             </div>
             <div className="flex flex-wrap gap-2 items-center">
                {holidays.map(h => (
                  <span key={h} className="bg-orange-50 text-orange-600 border border-orange-100 px-2 py-1 rounded-md text-[10px] font-bold flex items-center gap-2">
                    {new Date(h).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    <button onClick={() => removeHoliday(h)}><X size={10}/></button>
                  </span>
                ))}
                <label className="flex items-center gap-2 cursor-pointer ml-4">
                   <input type="checkbox" checked={includeSundays} onChange={e => setIncludeSundays(e.target.checked)} className="w-4 h-4 accent-blue-600" />
                   <span className="text-xs font-bold text-slate-600">Paid Sundays</span>
                </label>
             </div>
          </div>
        </div>
      </div>

      {/* 2. SUMMARY DASHBOARD */}
      <div className="grid grid-cols-1 mb-8">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-100">
           <div className="bg-slate-900 text-white p-4 flex justify-between items-center">
              <h3 className="font-bold flex items-center gap-2 text-sm tracking-wider"><Calculator size={16}/> MONTHLY SUMMARY</h3>
              <div className="flex gap-3">
                 <select value={selectedEmp} onChange={e=>setSelectedEmp(e.target.value)} className="bg-slate-800 border border-slate-700 text-xs rounded-lg px-3 py-2 outline-none text-white font-bold">
                    <option value="ALL">All Employees</option>
                    {summaryData.map(e => <option key={e.name} value={e.name}>{e.name}</option>)}
                 </select>
                 <button onClick={downloadPDF} className="bg-emerald-500 hover:bg-emerald-400 text-white px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-2 shadow-lg shadow-emerald-900/20 transition-all"><Download size={14}/> Download Report</button>
              </div>
           </div>
           
           <table className="w-full text-left">
              <thead className="bg-blue-50 text-slate-500 text-[10px] uppercase font-black tracking-widest border-b border-blue-100">
                <tr>
                  <th className="px-6 py-4">Employee</th>
                  <th className="px-6 py-4 text-center">Total Days</th>
                  <th className="px-6 py-4 text-center text-orange-600">Paid Offs</th>
                  <th className="px-6 py-4 text-center text-emerald-600">Present</th>
                  <th className="px-6 py-4 text-center text-red-500">Absent</th>
                  <th className="px-6 py-4 text-right">Payable</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {summaryData.map((data, idx) => (
                  <tr key={idx} className="hover:bg-slate-50/80 transition-colors group">
                    <td className="px-6 py-4 font-bold text-slate-700">{data.name}</td>
                    <td className="px-6 py-4 text-center font-mono text-slate-400 text-xs">{data.total_period}</td>
                    
                    {/* CLICKABLE: PAID LEAVES */}
                    <td className="px-6 py-4 text-center">
                      <button onClick={() => openModal(`${data.name} - Paid Leaves`, data.details.leaves, 'leave')} 
                        className="font-mono text-orange-600 font-bold bg-orange-50 px-3 py-1 rounded hover:bg-orange-100 hover:scale-110 transition-all">
                        {data.paid_leaves_count}
                      </button>
                    </td>

                    {/* CLICKABLE: PRESENT */}
                    <td className="px-6 py-4 text-center">
                       <button onClick={() => openModal(`${data.name} - Present Days`, data.details.present, 'present')} 
                        className="font-mono text-emerald-600 font-bold bg-emerald-50 px-3 py-1 rounded hover:bg-emerald-100 hover:scale-110 transition-all">
                        {data.worked_count}
                       </button>
                    </td>

                    {/* CLICKABLE: ABSENT */}
                    <td className="px-6 py-4 text-center">
                       <button onClick={() => openModal(`${data.name} - Absent Days`, data.details.absent, 'absent')} 
                        className={`font-mono font-bold px-3 py-1 rounded hover:scale-110 transition-all ${data.absent_count > 0 ? 'text-red-600 bg-red-50 hover:bg-red-100' : 'text-slate-300'}`}>
                        {data.absent_count}
                       </button>
                    </td>

                    <td className="px-6 py-4 text-right">
                       <span className="bg-slate-800 text-white px-4 py-1.5 rounded-lg font-bold text-sm shadow-md shadow-slate-300">
                         {data.payable_days} Days
                       </span>
                    </td>
                  </tr>
                ))}
              </tbody>
           </table>
        </div>
      </div>

      {/* 3. MODAL POPUP FOR DATES */}
      {modalData && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm animate-in fade-in zoom-in duration-200">
             <div className={`p-4 rounded-t-2xl flex justify-between items-center ${
                 modalData.type === 'absent' ? 'bg-red-50' : modalData.type === 'present' ? 'bg-emerald-50' : 'bg-orange-50'
             }`}>
                <h3 className={`font-black uppercase text-xs tracking-widest ${
                    modalData.type === 'absent' ? 'text-red-600' : modalData.type === 'present' ? 'text-emerald-600' : 'text-orange-600'
                }`}>{modalData.title}</h3>
                <button onClick={() => setModalData(null)} className="p-1 hover:bg-white rounded-full transition"><X size={18} className="text-slate-400"/></button>
             </div>
             
             <div className="p-6 max-h-[60vh] overflow-y-auto">
                {modalData.dates.length > 0 ? (
                    <div className="space-y-2">
                        {modalData.dates.map((d, i) => (
                            <div key={i} className="flex items-center gap-3 text-sm font-bold text-slate-600 border-b border-slate-50 pb-2 last:border-0">
                                <Calendar size={14} className="text-slate-400"/>
                                {d}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center text-slate-400 text-sm font-bold py-4">No dates found for this category.</div>
                )}
             </div>
             <div className="p-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
                <button onClick={() => setModalData(null)} className="w-full bg-white border border-slate-200 text-slate-600 font-bold py-2 rounded-xl text-sm hover:bg-slate-100">Close</button>
             </div>
          </div>
        </div>
      )}

      {/* 4. DETAILED LOGS (Bottom Section - Only Visible if needed, but data exists) */}
      <div className="text-center mt-10 mb-4 opacity-50 text-[10px] font-bold uppercase tracking-widest text-slate-400">
         Detailed logs are included in the PDF Export
      </div>

    </div>
  );
};

export default ReportPage;